import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getMyReport, getWaveformTechSummary } from "../lib/api";
import { useTechIdentity } from "../lib/TechIdentityContext";
import { techDisplayName } from "../lib/names";
import { normalizeDocs, computeIndividualData, IndividualPreview, exportPDF, caseScores, tierFor,
         INDIVIDUAL_REPORT_COLOR } from "./Audit";

/* A technologist's own Individual QA report for one month - the same report
   NP Reports builds for an admin, computed the same way. The backend sends this
   tech's cases in full and everyone else's as anonymous score rows, so the
   company comparison works without exposing anyone else's cases.

   The monthly email links here with ?month=YYYY-MM. */

function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function lastMonths(n) {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < n; i++) {
    d.setMonth(d.getMonth() - 1);
    out.push(monthKey(d));
  }
  return out;
}

function monthLabel(key) {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function monthRange(key) {
  const [y, m] = key.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return { startDate: `${key}-01`, endDate: `${key}-${String(last).padStart(2, "0")}` };
}

export default function MyReport() {
  const { effectiveWebId, loading: identityLoading } = useTechIdentity();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const months = useMemo(() => lastMonths(12), []);
  const month = /^\d{4}-\d{2}$/.test(params.get("month") || "") ? params.get("month") : months[0];

  const [raw, setRaw]         = useState(null);
  const [prevRaw, setPrevRaw] = useState(null);
  const [waveform, setWave]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (identityLoading) return;
    let cancelled = false;
    const { startDate, endDate } = monthRange(month);
    setLoading(true); setError(null);
    // One request: the backend returns last month's own cases alongside this
    // month's, from the same data build.
    Promise.all([
      getMyReport(month, effectiveWebId || ""),
      getWaveformTechSummary("", startDate, endDate, "", effectiveWebId || "").catch(() => null),
    ])
      .then(([r, w]) => { if (!cancelled) { setRaw(r); setWave(w); setPrevRaw({ documents: r?.previous_documents || [] }); } })
      .catch(e => { if (!cancelled) setError(e?.message || String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [month, effectiveWebId, identityLoading]);

  const data = useMemo(() => {
    if (!raw) return null;
    const techName = techDisplayName(raw.tech_name) || "";
    const docs = normalizeDocs(raw).documents || [];
    const config = { ...monthRange(month), region: "ALL", technologist: [], facility: [], procedureType: [],
                     companyName: raw.company_name || "" };
    if (!techName) return { techName: "You", noData: true, config, generatedAt: new Date().toLocaleString() };
    const prevDocs = prevRaw ? (normalizeDocs(prevRaw).documents || []) : [];
    return computeIndividualData(docs, techName, config, waveform, prevDocs);
  }, [raw, prevRaw, waveform, month]);

  const cases = useMemo(() => [...(data?.docs || [])]
    .sort((a, b) => String(b.service_date || "").localeCompare(String(a.service_date || ""))), [data]);

  return (
    <>
      <div className="page-header">
        <div className="page-title-group">
          <div className="page-title">My Monthly Report</div>
          <div className="page-subtitle">Your QA results for the month, compared with the company</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={month} onChange={e => setParams({ month: e.target.value })}
            style={{ fontSize: 12, fontFamily: "var(--font-mono)", padding: "6px 10px", borderRadius: 6,
              border: "1px solid var(--border)", background: "var(--white)", color: "var(--text-secondary)" }}>
            {months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        </div>
      </div>

      {/* page-body is the scroll container; the column layout lives on an inner
          wrapper so the cards keep their full height instead of shrinking to fit. */}
      <div className="page-body">
      <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 1100 }}>
        {loading ? (
          <div className="card" style={{ padding: 20 }}><div className="skeleton" style={{ height: 160, borderRadius: 8 }}/></div>
        ) : error ? (
          <div className="card" style={{ padding: "28px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>Couldn't load your report</div>
            <div style={{ fontSize: 12.5, color: "var(--text-tertiary)" }}>
              {/403|Forbidden|linked/.test(error)
                ? "Your account isn't linked to a technologist record. Contact an administrator."
                : error}
            </div>
          </div>
        ) : (
          <>
            <div className="card">
              <IndividualPreview data={data} color={INDIVIDUAL_REPORT_COLOR}
                waveformLoading={false} onExportPDF={() => exportPDF(data)} generating={false} hideExport />
            </div>

            {cases.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <div>
                    <div className="card-title">Your cases in {monthLabel(month)}</div>
                    <div className="card-subtitle">Open a case to see each result. You can flag any result you think is wrong.</div>
                  </div>
                </div>
                <div style={{ overflowX: "auto" }}>
                <table style={{ minWidth: 640 }}>
                  <thead>
                    <tr>
                      <th>PID</th><th>Date</th><th>Procedure</th>
                      <th style={{ textAlign: "center" }}>Documentation</th>
                      <th style={{ textAlign: "center" }}>Waveform</th>
                      <th style={{ textAlign: "center" }}>Total</th>
                      <th/>
                    </tr>
                  </thead>
                  <tbody>
                    {cases.map(d => {
                      const s = caseScores(d);
                      const cell = v => v == null
                        ? <td style={{ textAlign: "center", color: "var(--text-tertiary)" }}>—</td>
                        : <td style={{ textAlign: "center", fontWeight: 600, color: tierFor(v).color }}>{v}%</td>;
                      const caseLink = `/documents/${encodeURIComponent("PID_" + d.patient_id)}`;
                      return (
                        <tr key={d.file_name || d.patient_id}>
                          <td style={{ fontFamily: "var(--font-mono)", fontSize: 11.5 }}>{d.patient_id}</td>
                          <td>{d.service_date || "—"}</td>
                          <td>{d.procedure_type}</td>
                          {cell(s.doc)}{cell(s.wave)}{cell(s.total)}
                          <td style={{ textAlign: "right" }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => navigate(caseLink)}>Review</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            )}

            {data && !data.noData && (
              <div style={{ display: "flex", gap: 10 }}>
                <button className="btn btn-primary" onClick={() => exportPDF(data)} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"/></svg>
                  Export PDF
                </button>
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </>
  );
}
